import { useMemo, useRef, useState } from "react";
import { FixedSizeList as List } from "react-window";
import { Check, ChevronsUpDown, Search } from "lucide-react";
import { cn } from "@lab/src/lib/util";
import { useCentralStore } from "../../../store/Central";
import { NO_CLIENT_SCOPE } from "../../../store/Archive/archive.store";
import {
	filterActiveClients,
	getClientDisplayName,
} from "@/lib/utils/client-utils";
import { Button } from "../../../components/ui/button";
import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from "../../../components/ui/popover";

const RECENTS_KEY = "archive_recent_clients";
const MAX_RECENTS = 5;
const ROW_HEIGHT = 44;

const SCOPE_OPTIONS: { value: string | null; label: string; testId: string }[] =
	[
		{
			value: null,
			label: "Alle Auftraggeber",
			testId: "archive-scope-all",
		},
		{
			value: NO_CLIENT_SCOPE,
			label: "Ohne Auftraggeber",
			testId: "archive-scope-no-client",
		},
	];

const readRecents = (): string[] => {
	try {
		const raw = localStorage.getItem(RECENTS_KEY);
		return raw ? (JSON.parse(raw) as string[]) : [];
	} catch {
		return [];
	}
};

/** Records a just-picked customer as most-recent so repeat switching is one click. */
export const pushRecentClient = (clientId: string) => {
	try {
		const next = [
			clientId,
			...readRecents().filter((id) => id !== clientId),
		].slice(0, MAX_RECENTS);
		localStorage.setItem(RECENTS_KEY, JSON.stringify(next));
	} catch {
		// localStorage unavailable — recents are a convenience, ignore.
	}
};

interface ArchiveClientComboboxProps {
	/** Archive scope: null = all files, NO_CLIENT_SCOPE, or a client id. */
	value: string | null;
	onSelect: (scope: string | null) => void;
	includeInactive?: boolean;
}

export const ArchiveClientCombobox: React.FC<ArchiveClientComboboxProps> = ({
	value,
	onSelect,
	includeInactive = false,
}) => {
	const clientsLookup = useCentralStore((state) => state.clientsLookup);
	const [open, setOpen] = useState(false);
	const [term, setTerm] = useState("");
	const [highlight, setHighlight] = useState(0);
	const listRef = useRef<List>(null);

	const selected = value ? clientsLookup[value] : undefined;
	const scopeOption = SCOPE_OPTIONS.find((option) => option.value === value);

	const results = useMemo(() => {
		const all = includeInactive
			? Object.values(clientsLookup)
			: filterActiveClients(Object.values(clientsLookup));

		const lower = term.trim().toLowerCase();
		if (lower) {
			return all.filter((client) => {
				const name = getClientDisplayName(client).toLowerCase();
				return (
					name.includes(lower) ||
					(client.code ?? "").toLowerCase().includes(lower)
				);
			});
		}
		// No search term: show recents first, then the rest.
		const recents = readRecents()
			.map((id) => clientsLookup[id])
			.filter(Boolean);
		const recentIds = new Set(recents.map((c) => c.id));
		return [...recents, ...all.filter((c) => !recentIds.has(c.id))];
	}, [clientsLookup, term, includeInactive]);

	const showRecentsHeader = term.trim() === "" && readRecents().length > 0;

	const handleSelect = (clientId: string) => {
		pushRecentClient(clientId);
		onSelect(clientId);
		setOpen(false);
		setTerm("");
		setHighlight(0);
	};

	const handleSelectScope = (scope: string | null) => {
		onSelect(scope);
		setOpen(false);
	};

	const handleOpenChange = (next: boolean) => {
		setOpen(next);
		if (next) {
			setTerm("");
			setHighlight(0);
		}
	};

	const handleKeyDown = (event: React.KeyboardEvent) => {
		if (event.key === "ArrowDown") {
			event.preventDefault();
			setHighlight((h) => {
				const next = Math.min(h + 1, results.length - 1);
				listRef.current?.scrollToItem(next);
				return next;
			});
		} else if (event.key === "ArrowUp") {
			event.preventDefault();
			setHighlight((h) => {
				const next = Math.max(h - 1, 0);
				listRef.current?.scrollToItem(next);
				return next;
			});
		} else if (event.key === "Enter") {
			event.preventDefault();
			const client = results[highlight];
			if (client?.id) handleSelect(client.id);
		}
	};

	return (
		<Popover open={open} onOpenChange={handleOpenChange}>
			<PopoverTrigger asChild>
				<Button
					variant="outline"
					className="min-w-[16rem] justify-between"
					aria-label="Auftraggeber auswählen"
					data-testid="archive-client-select-button"
				>
					<span className="truncate">
						{selected
							? getClientDisplayName(selected)
							: scopeOption?.label}
					</span>
					<ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
				</Button>
			</PopoverTrigger>
			<PopoverContent className="w-[26rem] p-0" align="start">
				<div className="flex items-center gap-2 border-b px-3 py-2">
					<Search className="h-4 w-4 shrink-0 text-gray-400" />
					<input
						autoFocus
						value={term}
						onChange={(e) => {
							setTerm(e.target.value);
							setHighlight(0);
						}}
						onKeyDown={handleKeyDown}
						placeholder="Kunde suchen…"
						className="w-full bg-transparent text-sm outline-none"
						aria-label="Kunde suchen"
						data-testid="archive-client-search"
					/>
				</div>
				{term.trim() === "" && (
					<div className="border-b py-1">
						{SCOPE_OPTIONS.map((option) => (
							<button
								type="button"
								key={option.testId}
								className="flex h-9 w-full items-center gap-2 px-3 text-left text-sm hover:bg-accent"
								onClick={() => handleSelectScope(option.value)}
								aria-label={option.label}
								data-testid={option.testId}
							>
								<Check
									className={cn(
										"h-4 w-4 shrink-0",
										option.value === value
											? "opacity-100"
											: "opacity-0"
									)}
								/>
								{option.label}
							</button>
						))}
					</div>
				)}
				{showRecentsHeader && (
					<div className="px-3 pt-2 text-xs font-semibold uppercase tracking-wide text-gray-400">
						Zuletzt benutzt
					</div>
				)}
				{results.length === 0 ? (
					<div className="px-3 py-6 text-center text-sm text-gray-500">
						Kein Kunde gefunden
					</div>
				) : (
					<List
						ref={listRef}
						height={Math.min(results.length * ROW_HEIGHT, 320)}
						itemCount={results.length}
						itemSize={ROW_HEIGHT}
						width="100%"
					>
						{({ index, style }) => {
							const client = results[index];
							const isSel = client.id === value;
							return (
								<button
									type="button"
									key={client.id}
									style={style}
									className={cn(
										"flex w-full items-center gap-2 px-3 text-left text-sm hover:bg-accent",
										index === highlight && "bg-accent"
									)}
									onMouseEnter={() => setHighlight(index)}
									onClick={() =>
										client.id && handleSelect(client.id)
									}
									data-testid="archive-client-option"
								>
									<Check
										className={cn(
											"h-4 w-4 shrink-0",
											isSel ? "opacity-100" : "opacity-0"
										)}
									/>
									<span className="min-w-0 flex-1 truncate">
										{getClientDisplayName(client)}
									</span>
									<span className="shrink-0 text-xs text-gray-400">
										{client.code}
									</span>
									{client.is_inactive && (
										<span className="shrink-0 text-xs text-gray-400">
											(inaktiv)
										</span>
									)}
								</button>
							);
						}}
					</List>
				)}
			</PopoverContent>
		</Popover>
	);
};
